import { useQueryClient } from '@tanstack/react-query';
import { ZfDropdown, ZfDropdownItem } from '@zafire/react';
import type { ComponentProps, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { queryKeys, useUserDetails } from '../../api';
import type { UserRole } from '../../api/generated/client';
import { useAppEnvironment } from '../../contexts/app-environment-context';
import { useWealthPlanningNavigate } from '../../hooks/useWealthPlanningNavigate';
import { wnSendTrace } from '../../utils/wn-event-utils';

const roleMapTranslation: Record<string, string> = {
    ['CONTENT']: 'roleContentAdmin',
    ['RM']: 'roleRm',
    ['WP']: 'roleWp',
    ['EVERYONE']: 'roleGuest',
};

interface IRoleSwitchProps {
    isContentAdminRoleEnabled?: boolean;
}

export const RoleSwitch: FunctionComponent<IRoleSwitchProps> = ({ isContentAdminRoleEnabled = true }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { navigateToBase } = useWealthPlanningNavigate();
    const { localStore } = useAppEnvironment();
    const { data: user } = useUserDetails();

    if (!user?.roles || user?.roles.length < 2) {
        return null;
    }

    const roleOptions = user.roles.map((role) => ({
        value: role,
        label: t(roleMapTranslation[role]),
    }));

    const handleChangeRole = (role: UserRole) => {
        if (role === 'CONTENT') navigate('/content-admin');
        else navigateToBase();

        localStore.storeUserRole(role);
        queryClient.setQueryData(queryKeys.user(), { ...user, currentRole: role });
        wnSendTrace({ feature: 'general', tags: { partnerUuid: '', action: 'change-role', role } });
    };

    function getDropdownState(role: UserRole): ComponentProps<typeof ZfDropdownItem>['state'] {
        if (role === user?.currentRole) return 'active';

        if (!isContentAdminRoleEnabled && role === 'CONTENT') return 'disabled';

        return 'default';
    }

    return (
        <ZfDropdown icon="settings" hierarchy="tertiary" size="medium">
            {roleOptions.map((role) => (
                <ZfDropdownItem
                    state={getDropdownState(role.value)}
                    key={role.value}
                    label={role.label}
                    onDropdownItemSelect={() => handleChangeRole(role.value)}
                />
            ))}
        </ZfDropdown>
    );
};
